import { ElementRef, Injectable } from '@angular/core';
import { MapGeocoder, MapGeocoderResponse } from '@angular/google-maps';
import { iif, Observable, of, switchMap, throwError } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';
import { GoogleMapsApiService } from './google-maps-api.service';
import PlaceResult = google.maps.places.PlaceResult;

@Injectable()
export class GoogleMapsUtilsService {

  constructor(
    private readonly geocoder: MapGeocoder,
    private readonly googleMapsApiService: GoogleMapsApiService,
  ) {
  }

  /*
  * GEOLOCATE
  * */
  geolocateMe(): Observable<PlaceResult> {
    if (navigator.geolocation) {
      return this.getLocation().pipe(
        switchMap((position: GeolocationPosition) => {
          return iif(
            () => !!position,
            this.googleMapsApiService.googleApiLoaded$.pipe(
              filter((loaded) => !!loaded),
              switchMap(() => {
                const lat = position.coords.latitude;
                const lng = position.coords.longitude;
                return this.geocoder.geocode({location: {lat, lng}})
              }),
              map((mapGeocoderResponse: MapGeocoderResponse) => {
                if (mapGeocoderResponse.status === 'OK') {
                  if (mapGeocoderResponse.results[5]) {
                    return mapGeocoderResponse.results[5] as PlaceResult;
                  } else {
                    console.error('No results found');
                    return null;
                  }
                } else {
                  console.error('Geocoder failed due to: ' + mapGeocoderResponse.status);
                  throwError('Geocoder failed due to: ' + mapGeocoderResponse.status);
                }
              })
            ),
            of(null),
          )
        }),
      )
    } else {
      alert("Geolocation is not supported by this browser.");
      return throwError('Geolocation is not supported by this browser.');
    }
  }

  /*
  * GET GEOLOCATION AS OBSERVABLE
  * */
  getLocation(): Observable<GeolocationPosition> {
    return new Observable(obs => {
      navigator.geolocation.getCurrentPosition(
        success => {
          obs.next(success);
          obs.complete();
        },
        error => {
          obs.error(error);
        }
      );
    });
  }

  geoCode(address: string) {
    this.geocoder.geocode({
      address,
    }).subscribe();
  }

  /*
  * GET PLACE AUTOCOMPLETE
  * */
  getPlaceAutocomplete(element: ElementRef): Observable<PlaceResult> {
    const options = {
      componentRestrictions: {
        country: ['FR']
      },
      types: ['(regions)']
    };
    const autocomplete = new google.maps.places.Autocomplete(element.nativeElement, options);
    return this.getPlaceChanged(autocomplete);
  }

  /*
  * GET PLACE CHANGED AS OBS
  * */
  getPlaceChanged(autocomplete: any): Observable<PlaceResult> {
    return new Observable(obs => {
      google.maps.event.addListener(autocomplete, 'place_changed', () => {
          obs.next(autocomplete.getPlace());
        },
      );
    });
  }

  getAddrComponent(place: any, componentTemplate: any): any {
    let result;
    for (const component of place.address_components) {
      const addressType = component.types[0];
      if (componentTemplate[addressType]) {
        result = component[componentTemplate[addressType]];
        return result;
      }
    }
  }
  getStreetNumber(place: any): any {
    const COMPONENT_TEMPLATE = { street_number: 'short_name' };
    return this.getAddrComponent(place, COMPONENT_TEMPLATE);
  }
  getStreet(place: any): any {
    const COMPONENT_TEMPLATE = { route: 'long_name' };
    return this.getAddrComponent(place, COMPONENT_TEMPLATE);
  }
  getCity(place: any): any {
    const COMPONENT_TEMPLATE = { locality: 'long_name' };
    return this.getAddrComponent(place, COMPONENT_TEMPLATE);
  }
  getState(place: any): any {
    const COMPONENT_TEMPLATE = { administrative_area_level_1:  'short_name' };
    return this.getAddrComponent(place, COMPONENT_TEMPLATE);
  }
  getDistrict(place: any): any {
    const COMPONENT_TEMPLATE = { administrative_area_level_2:
        'short_name' };
    return this.getAddrComponent(place, COMPONENT_TEMPLATE);
  }
  getCountryShort(place: any): any {
    const COMPONENT_TEMPLATE = { country: 'short_name' };
    return this.getAddrComponent(place, COMPONENT_TEMPLATE);
  }
  getCountry(place: any): any {
    const COMPONENT_TEMPLATE = { country: 'long_name' };
    return this.getAddrComponent(place, COMPONENT_TEMPLATE);
  }
  getPostCode(place: any): any {
    const COMPONENT_TEMPLATE = { postal_code: 'long_name' };
    return this.getAddrComponent(place, COMPONENT_TEMPLATE);
  }
  getPhone(place: any): any {
    const COMPONENT_TEMPLATE = { formatted_phone_number:
        'formatted_phone_number' };
    return this.getAddrComponent(place, COMPONENT_TEMPLATE);
  }
}
